import React from 'react'
import PropTypes from 'prop-types'
import I18n from 'translations/i18n'
import { useForm, useFormState } from 'react-final-form'
import { Typography } from '@jisr-hr/ds'
import { DatePicker } from '@jisr-hr/ds-beta'
import { SelectField } from 'components/final-form'
import { addDays, addMonths, differenceInDays, format, subDays } from 'date-fns'
import useUpdateEffect from 'components/global/hooks/useUpdateEffect'
import { required } from 'components/global/form/FormValidations'
import NewRadioField from 'components/final-form/NewRadioField'
import { loadEmployeePolices } from 'redux/setting/attendanceManagement/attendancePolicy/actionCreators'
import { useDispatch, useSelector } from 'utils/hooks'
import DSIcon from 'components/DSIcon'
import {
  getOvertimeRequestNewQueryKey,
  useGetOvertimeRequestNew,
} from 'apis/Attendance/overtimeRequest/useOvertimeRequest'
import { Loader } from 'components/global/JisrLoader/JisrLoader'
import { queryClient } from 'hooks/useReactQuery'
import { timeToNumbers } from 'components/global/form/formNormalize'

import SingleDay from './components/SingleDay'
import MultipleDays from './components/MultipleDays'
import { overtimeTypes, organizationPolicy } from './helper'

import { useRequest } from '../../../RequestProvider'
import Reason from '../Reason'
import { useRequestDetail } from '../../../hooks'

const OvertimeDuration = (props) => {
  const { overtimeDetail } = props
  const { vars } = useRequest()
  const { change } = useForm()
  const { values } = useFormState()
  const dispatch = useDispatch()

  const [date, setDate] = React.useState([])

  const currency = useSelector(
    (s) => s.organizationProfile?.organization_currency?.currency_type_i18n,
  )

  const isSaudiCurrency = useSelector(
    (s) => s.organizationProfile?.organization_currency?.currency_type === 'sar',
  )

  const attendanceEmployeePolicy = useSelector(
    ({ attendancePolicy }) => attendancePolicy.attendanceEmployeePolicy,
  )

  const { request } = useRequestDetail()
  const empId = vars.id ?? request?.employee?.id

  const overtime_logs_attributes = values.multiple_shifts?.reduce((prev, record) => {
    const minutes = timeToNumbers(record?.duration)?.totalMinutes
    if (minutes > 0) {
      return [
        ...prev,
        {
          date: record?.date,
          duration: record?.duration,
          ...(record?.detail?.employee_shift_id && {
            employee_shift_id: record.detail.employee_shift_id,
          }),
        },
      ]
    }
    return prev
  }, [])

  const { data, isFetching } = useGetOvertimeRequestNew(
    empId,
    {
      ...(request?.id && { request_id: request.id }),
      overtime_request: {
        overtime_type: values.overtime_type,
        overtime_logs_attributes:
          values.overtime_type === 'single_day'
            ? values.overtime_logs_attributes
            : overtime_logs_attributes,
        compensation: values.compensation,
      },
    },
    {
      query: {
        enabled: !!values.compensation && !!empId,
        select: (response) => response.data.data,
        onErrorCallback: () => {
          // Reset compensation to undefined on error
          change('compensation', undefined)
        },
      },
    },
  )

  const totalDuration = React.useMemo(
    () =>
      values?.multiple_shifts?.reduce((result, record) => {
        const minutes = timeToNumbers(record?.duration)?.totalMinutes || 0

        return result + minutes
      }, 0),
    [values?.multiple_shifts],
  )

  const resetDateTime = (d) => new Date(d?.setHours(0, 0, 0, 0))

  const handelResetValues = () => {
    change('initialMultipleShifts', undefined)
    change('initialOvertimeDates', undefined)
    change('multiple_shifts', [])
    change('overtime_dates', [])
    change('compensation', undefined)
    queryClient.removeQueries(
      getOvertimeRequestNewQueryKey(empId, values.compensation, request?.id),
    )
  }
  const handleDate = (_, v) => {
    handelResetValues()

    setDate(v)
  }

  const handleBulkDates = () => {
    // ? Reset the time for Date due to differenceInDays depend on both date and time
    // ? Increase difference by one day because differenceInDays ignore one day
    const difference = differenceInDays(resetDateTime(date?.[1]), resetDateTime(date?.[0])) + 1

    const dates = Array.from({ length: difference }).map((_, i) =>
      format(addDays(date?.[0], i), 'yyyy-MM-dd'),
    )

    change('overtime_dates', dates)
  }

  useUpdateEffect(() => {
    if (
      values?.overtime_type === 'bulk_overtime' &&
      date.length === 2 &&
      !values?.initialOvertimeDates
    ) {
      handleBulkDates()
    }
  }, [date])

  React.useEffect(() => {
    if (values?.initialOvertimeDates?.length > 0) {
      setDate([
        new Date(values?.initialOvertimeDates[0]),
        new Date(values?.initialOvertimeDates[values?.initialOvertimeDates.length - 1]),
      ])
    }
  }, [values?.initialOvertimeDates])

  React.useEffect(() => {
    dispatch(
      loadEmployeePolices(empId, {
        policy_type: 'overtime',
      }),
    )
  }, [])

  return (
    <div className="flex flex-col gap-8">
      {isFetching && <Loader fixed />}
      <div className="flex flex-col">
        <div className="flex flex-col gap-4">
          <div className={values?.overtime_type === 'bulk_overtime' ? 'col-span-6' : 'col-span-12'}>
            <SelectField
              label={I18n.t('type')}
              name="overtime_type"
              options={overtimeTypes}
              disabled={vars?.shiftView}
              onChange={() => {
                handelResetValues()
                change('overtime_logs_attributes', [
                  values?.overtime_type === 'single_day' ? overtimeDetail : {},
                ])
              }}
            />
          </div>
          {values?.overtime_type === 'bulk_overtime' && (
            <div className="col-span-6">
              <DatePicker
                calenderProps={{
                  type: 'range',
                  minDate: subDays(new Date(), 59),
                  maxDate: addMonths(new Date(), 3),
                }}
                label={I18n.t('date_range')}
                triggerType="input"
                onChange={handleDate}
                value={date.length === 2 ? date : undefined}
              />
            </div>
          )}
          {values?.overtime_type === 'single_day' && <SingleDay />}
          {['multiple_days', 'bulk_overtime'].includes(values?.overtime_type) && (
            <MultipleDays
              totalDuration={totalDuration}
              handleDate={(d) => setDate([d])}
            />
          )}
        </div>
        {/* <Typography
          variant="body-new/semibold"
          text={I18n.t('label.overtime_works_on')}
        />
        <SelectField
          label={I18n.t('label.overtime_works_on')}
          name="overtime_works_on"
          options={worksOnThis}
          disabled={vars?.shiftView}
        /> */}
      </div>
      {(attendanceEmployeePolicy?.is_overtime_enabled ||
        attendanceEmployeePolicy?.is_time_off_in_lieu_enabled) && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Typography
              variant="body-new/semibold"
              text={I18n.t('label.you_want_this_to_be_title')}
            />
            <Typography
              variant="caption"
              text={I18n.t('label.you_want_this_to_be_description')}
            />
          </div>
          <NewRadioField
            labelKey="title"
            valueKey="id"
            options={organizationPolicy({
              is_overtime_enabled: attendanceEmployeePolicy?.is_overtime_enabled,
              is_time_off_in_lieu_enabled: attendanceEmployeePolicy?.is_time_off_in_lieu_enabled,
            })}
            name="compensation"
            value={values?.compensation}
            validate={required}
            disabled={
              values?.overtime_type === 'single_day'
                ? !(
                    timeToNumbers(values?.overtime_logs_attributes?.[0]?.duration).totalMinutes &&
                    values?.overtime_logs_attributes?.[0]?.date
                  )
                : !(
                    values?.multiple_shifts?.every(
                      (shift) => timeToNumbers(shift?.duration).totalMinutes > 0,
                    ) && totalDuration
                  )
            }
            gap={16}
          />
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <DSIcon name="clock-timer" />
              <Typography
                text={I18n.t('hourly_rate')}
                variant="subtitle-1"
              />
            </div>
            <div className="flex gap-3 items-center">
              <Typography
                text={`${data?.overtime_request?.hour_rate || '--'} x`}
                variant="body-new/bold"
              />
            </div>
          </div>
          <div className="flex justify-between items-center border-0 border-t border-solid  !border-color-border-lighter py-6">
            <div className="flex gap-2">
              <DSIcon name="calendar" />
              <Typography
                text={I18n.t('label.total')}
                variant="subtitle-1"
              />
            </div>
            <Typography
              text={
                values.compensation === 'time_off_in_lieu'
                  ? I18n.t('label.total_toil_compensation_days', {
                      count: data?.overtime_request?.total_cost_of_hours?.toFixed(2) ?? '--',
                    })
                  : `${
                      isSaudiCurrency && values.compensation === 'paid_overtime'
                        ? currency ?? ''
                        : ''
                    } ${data?.overtime_request?.total_cost_of_hours ?? values?.hour_rate ?? '--'} ${
                      !isSaudiCurrency && values.compensation === 'paid_overtime'
                        ? currency ?? ''
                        : ''
                    }`
              }
              variant="body-new/bold"
            />
          </div>
        </div>
      )}
      <Reason />
    </div>
  )
}

OvertimeDuration.propTypes = {
  overtimeDetail: PropTypes.shape(),
}

export default OvertimeDuration
